<template>
  <v-container grid-list-xs fluid>
    <v-row row wrap justify="center">
      <v-col class="col-7">
        How often do you want your notification triggered?
      </v-col>
      <v-col class="col-5">
        <v-select
          v-model="notification.frequency"
          :items="frequencies"
          item-value="adverb"
          item-text="display"
          class="mt-0 pt-0"
          @change="handleChangeFrequency"
        ></v-select>
      </v-col>
    </v-row>
    <v-row
      v-if="
        notification.frequency &&
        notification.frequency.toUpperCase() === 'WEEKLY'
      "
      row
      wrap
      justify="center"
    >
      <v-col class="col-7">
        What day of the week do you want your notification triggered?
      </v-col>
      <v-col class="col-5">
        <v-select
          v-model="notification.dayOfWeek"
          :items="daysOfWeek"
          class="mt-0 pt-0"
        ></v-select>
      </v-col>
    </v-row>
    <v-row
      v-if="
        notification.frequency &&
        (notification.frequency.toUpperCase() === 'DAILY' ||
          notification.frequency.toUpperCase() === 'WEEKLY')
      "
      row
      wrap
      justify="center"
    >
      <v-col class="col-7">
        What time of the day do you want your notification triggered?
      </v-col>
      <v-col class="col-5">
        <TimeSelector
          :time="timeOfDay"
          @change="handleChangeTime"
        ></TimeSelector>
      </v-col>
    </v-row>
    <v-row row wrap justify="center">
      <v-col class="col-7">
        When do you want this notification to expire?
      </v-col>
      <v-col class="col-5">
        <v-select
          v-model="notification.expiration"
          :items="expirations"
          item-value="value"
          item-text="name"
          :messages="expirationMessage"
          :error-messages="expirationMessageError"
          class="mt-0 pt-0"
        ></v-select>
      </v-col>
    </v-row>
    <v-row row wrap justify="center">
      <v-col class="col-7">
        How do you want this notification delivered?
      </v-col>
      <v-col class="col-5">
        <div>
          <v-checkbox
            v-model="notification.delivery"
            label="Email"
            value="email"
            class="pull-left mt-0 pt-0 mr-5"
          ></v-checkbox>
          <v-checkbox
            v-model="notification.delivery"
            label="Push"
            value="push"
            class="pull-left mt-0 pt-0"
          ></v-checkbox>
          <div class="clearfix"></div>
        </div>
        <div>
          <v-checkbox
            v-if="targets && targets.length"
            v-model="notification.delivery"
            label="Targets"
            value="target"
            class="pull-left mt-0 pt-0 mr-5"
          ></v-checkbox>

          <v-select
            v-if="targets && targets.length"
            v-model="notification.targets"
            :disabled="!notification.delivery.includes('target')"
            placeholder="Select targets"
            :items="targets"
            item-text="name"
            item-value="targetId"
            multiple
            class="pull-left mt-0 pt-0 hyp-targets"
          >
            <template v-slot:item="{ active, item, attrs, on }">
              <v-list-item v-on="on" v-bind="attrs" #default="{ active }">
                <v-checkbox :input-value="active"></v-checkbox> {{ item.name }}
              </v-list-item>
            </template>
            <template v-slot:selection="{ item, index }">
              <span v-if="index === 0">
                {{ notification.targets.length }} selected
              </span>
            </template>
          </v-select>
          <div class="clearfix"></div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import lodashCloneDeep from "lodash/cloneDeep";
import lodashRemove from "lodash/remove";
import dayjs from "dayjs";

import { getFormattedDate, nowUtc } from "@/common/date.utilities";
import { DAYS_OF_WEEK } from "@/common/constants/misc";
import {
  NOTIFICATION_FREQUENCY_OPTIONS,
  NOTIFICATION_FREQUENCY_MONTHLY,
} from "@/common/constants/notifications";
import {
  updateStoreStep,
  extractDetailsFromSavedSearch,
} from "@/services/search/savedSearch.service";
import { getTargets } from "@/services/targets/target.service";

export default {
  name: "NotificationsStepFrequency",
  components: {
    TimeSelector: () => import("@/components/common/TimeSelector"),
  },
  props: {
    savedSearch: {
      type: Object,
    },
    refresh: {
      type: String,
    },
  },
  data() {
    const daysOfWeek = DAYS_OF_WEEK.map((day) => {
      return day.name;
    });
    const expirationDefault = 1;
    const expirationDateDefault = dayjs()
      .add(expirationDefault, "week")
      .toISOString();

    const notification = {
      frequency: "IMMEDIATELY",
      dayOfWeek: daysOfWeek[new Date().getDay()],
      timeOfDay: null,
      expiration: expirationDefault,
      expirationDate: expirationDateDefault,
      delivery: ["email"],
    };

    let frequencies = lodashCloneDeep(NOTIFICATION_FREQUENCY_OPTIONS);
    if (!this.featureUnlocked("notifications")) {
      frequencies = lodashRemove(frequencies, (f) => {
        return f.adverb !== NOTIFICATION_FREQUENCY_MONTHLY;
      });
    }

    const expirationOptions = [
      { id: 1, name: "1 week", value: 1, type: "weeks" },
      { id: 2, name: "2 weeks", value: 2, type: "weeks" },
      { id: 3, name: "3 weeks", value: 3, type: "weeks" },
      { id: 4, name: "Never", value: "never", type: null },
    ];

    return {
      notification: notification,
      frequencies: frequencies,
      daysOfWeek: daysOfWeek,
      timeToNow: null,
      expirations: expirationOptions,
      targets: null,
    };
  },
  computed: {
    expirationMessage() {
      if (this.notification.expirationDate && !this.isExpired) {
        return `Expires ${getFormattedDate(this.notification.expirationDate)}`;
      }
      return null;
    },
    expirationMessageError() {
      if (this.notification.expirationDate && this.isExpired) {
        return `Notification expired on ${getFormattedDate(
          this.notification.expirationDate
        )}`;
      }
      return null;
    },
    isExpired() {
      if (
        this.notification.expirationDate &&
        dayjs(nowUtc()).isAfter(this.notification.expirationDate)
      ) {
        return true;
      }
      return false;
    },
    timeOfDay() {
      if (this.notification.timeOfDay) {
        return this.notification.timeOfDay;
      }
      return null;
    },
  },
  mounted() {
    extractDetailsFromSavedSearch(this.notification, "frequency");
    this.update();
  },
  methods: {
    update() {
      updateStoreStep(this.notification, "frequency");
    },
    handleChangeTime(time) {
      this.notification.timeOfDay = time;
      this.update();
    },
    handleChangeFrequency() {
      this.notification.dayOfWeek = this.daysOfWeek[new Date().getDay()];
      const currHour = new Date().getHours();
      const nowHour = currHour > 9 ? currHour : `0${currHour}`;
      this.notification.timeOfDay = `${nowHour}:00`;
      if (
        this.notification.frequency.toUpperCase() === "IMMEDIATELY" ||
        this.notification.frequency.toUpperCase() === "HOURLY"
      ) {
        this.notification.dayOfWeek = null;
        this.notification.timeOfDay = null;
      } else if (this.notification.frequency.toUpperCase() === "DAILY") {
        this.notification.dayOfWeek = null;
      }
      this.update();
    },
  },
  watch: {
    "notification.frequency"() {
      this.update();
    },
    "notification.dayOfWeek"() {
      this.update();
    },
    "notification.expiration"() {
      this.notification.expirationDate = null;
      if (this.notification.expiration !== "never") {
        this.notification.expirationDate = dayjs()
          .add(this.notification.expiration, "week")
          .toISOString();
      }
      this.update();
    },
    "notification.delivery": {
      handler() {
        this.update();
      },
      deep: true,
    },
    "notification.targets": {
      handler() {
        this.update();
      },
      deep: true,
    },
    refresh: {
      async handler() {
        this.targets = await getTargets();
        extractDetailsFromSavedSearch(this.notification, "frequency");
        this.update();
      },
      immediate: true,
    },
  },
  beforeDestroy() {},
};
</script>

<style>
.hyp-targets .v-application--is-ltr .v-list-item__action:first-child,
.v-application--is-ltr .v-list-item__icon:first-child {
  margin-right: 12px !important;
}
</style>
<style scoped>
.hyp-targets {
  position: relative;
  top: -5px;
}
</style>
